import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';

import authService from '@services/admin/authService';

import adminCable from '@/lib/adminCable';

/** @typedef {import('@/permissions_manifest.generated').Permission} Permission */

// 認證狀態
/** @type {{ isAuthenticated: boolean, user: any, permissions: Permission[], loading: boolean, error: string | null }} */
const initialState = {
  isAuthenticated: false,
  user: null,
  permissions: [], // 用戶權限清單 (例如：["products:read", "products:write"])
  loading: true,
  error: null
};

// Actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_PERMISSIONS: 'SET_PERMISSIONS'
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        permissions: action.payload.user?.permissions || [],
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: action.payload.error
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        permissions: [],
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    case AUTH_ACTIONS.SET_PERMISSIONS:
      return {
        ...state,
        permissions: action.payload
      };
    default:
      return state;
  }
}

// 創建Context
const AuthContext = createContext();

// AuthProvider組件
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 初始化檢查認證狀態
  useEffect(() => {
    // ✅ 在公開頁面（登入）不需要檢查認證狀態
    const isGuestPage = window.location.pathname.includes('/login');

    if (isGuestPage) {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    } else {
      checkAuthStatus();
    }
  }, []);

  // 檢查認證狀態
  const checkAuthStatus = async () => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      // ✅ 直接使用 fetch（不透過 adminApiFetch）因為這個 endpoint 不在 /api/admin/v1/ 下
      const response = await fetch('/api/user/v1/me', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      // ✅ 處理 401 錯誤
      if (response.status === 401) {
        authService.clearAuth();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        // 不需要手動重定向，AuthGuard 會處理路由跳轉到 /login
        return;
      }

      const result = await response.json();

      if (result.success && result.data && result.data.authenticated) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: result.data.user }
        });
      } else {
        // 用戶未認證
        authService.clearAuth();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // 網路錯誤時也要確保清空認證狀態
      authService.clearAuth();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // 登入
  const login = useCallback(async (email, password) => {
    // 🔥 不使用 LOGIN_START（會設 loading: true），避免 GuestGuard 卸載 Login 組件
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

    try {
      const data = await fetch('/api/user/v1/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const result = await data.json();

      // TOTP 二步驟驗證：密碼正確但需要 TOTP
      if (result.success && result.data?.totp_required) {
        return {
          success: false,
          totpRequired: true,
          pendingToken: result.data.pending_token
        };
      }

      if (result.success && result.data) {
        // 登入成功時設定用戶資料 (token 已經存在 HTTP-only cookie 中)
        authService.setUser(result.data.user);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: result.data.user }
        });
        return {
          success: true,
          message: result.message,
          totpSetupRequired: result.data.user?.totp_setup_required || false
        };
      }
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: result.message || 'authentication_failed' }
      });
      return { success: false, message: result.message || 'authentication_failed' };

    } catch (error) {
      const errorMessage = 'authentication_failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, message: errorMessage };
    }
  }, []);

  // TOTP 驗證（登入第二步）
  const verifyTotp = useCallback(async (pendingToken, code) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const result = await authService.verifyTotp(pendingToken, code);
      if (result.success && result.data) {
        authService.setUser(result.data.user);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: result.data.user }
        });
        return {
          success: true,
          message: result.message,
          totpSetupRequired: result.data.user?.totp_setup_required || false
        };
      }
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: result.message || 'invalid_totp_code' }
      });
      return { success: false, message: result.message || 'invalid_totp_code' };
    } catch (error) {
      const errorMessage = 'authentication_failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, message: errorMessage };
    }
  }, []);

  // 快速登入（開發環境專用）
  const quickLogin = async () => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const data = await fetch('/api/user/v1/quick_login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await data.json();
      if (result.success && result.data) {
        // 登入成功時設定用戶資料 (token 已經存在 HTTP-only cookie 中)
        authService.setUser(result.data.user);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: result.data.user }
        });
        return { success: true, message: result.message };
      }
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: result.message }
      });
      return { success: false, message: result.message };

    } catch (error) {
      const errorMessage = 'authentication_failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, message: errorMessage };
    }
  };

  // 登出
  const logout = async () => {
    try {
      await fetch('/api/user/v1/logout', {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // ✅ 登出時強制斷開 WebSocket 連接
      adminCable.forceDisconnect();
      authService.clearAuth();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // 清除錯誤
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // 設定權限（由 MerchantContext 呼叫）
  const setPermissions = (permissions) => {
    dispatch({ type: AUTH_ACTIONS.SET_PERMISSIONS, payload: permissions });
  };

  // 權限檢查改由 useCan hook 統一處理（@shared/permissions/useCan）。
  // 不再從 context 暴露 hasPermission 方法。

  const value = {
    ...state,
    login,
    verifyTotp,
    quickLogin,
    logout,
    clearError,
    checkAuthStatus,
    setPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// 自定義Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;